/*
 * Developed for G.J. Gardner Homes by Softeq Development Corporation
 * http://www.softeq.com
 */

import { findIndex, identity, isFunction, keyBy, reduceRight, sortedIndexBy } from 'lodash';
import { setEntityFields } from './entity/entity.utils';

export type ArrayEntityTransform<T, S> = (oldData: T, selector: S) => T;
export type ArrayEntityMerge<T, S> = (oldData: T, newData: T, selector: S) => T;

/**
 * This helper provides immutable operations over array structure.
 * Implementations of this interface can follow different strategies,
 * for example it can provide the simplest implementation or guarantee
 * that order of items satisifies some order defined by comparator function.
 */
export interface ArrayHelper<T, S = T> {
  /**
   * Adds items into the beginning of the array;
   */
  prepend(array: T[], ...items: T[]): T[];

  /**
   * Adds items into the end of the array.
   */
  append(array: T[], ...items: T[]): T[];

  /**
   * Adds item by the given index.
   */
  insert(array: T[], index: number, item: T): T[];

  /**
   * Adds item by the given index.
   */
  insertSorted(array: T[], item: T, sortBy: TransformFn<T, any> | string): T[];

  /**
   * Updates item in the array. Item is searched using defined selector.
   * In practice selector can be an item itself or its ID.
   */
  update(array: T[], selector: S, item: T | IdentityFn<T>): T[];

  /**
   * Updates items in the array.
   */
  updateAll(array: T[], transform: (item: any, index: number) => any): T[];

  /**
   * Updates items in the array.
   */
  updateAllRight(array: T[], transform: (item: any, index: number) => any): T[];

  /**
   * Merges item in the array. Item is searched using defined selector.
   * In practice selector can be an item itself or its ID.
   */
  merge(array: T[], selector: S, item: Partial<T> | ArrayEntityTransform<T, S>): T[];

  /**
   * Removes item from the array. Item is searched using defined selector.
   * In practice selector can be an item itself or its ID.
   */
  remove(array: T[], selector: S): T[];

  /**
   * Moves item from the given index to another one.
   */
  move(array: T[], from: number, to: number): T[];

  /**
   * Returns index of selector in the given array
   */
  indexOf(array: T[], selector: S): number;

  /**
   * Returns array item satisfied given selector or undefined
   */
  find(array: T[], selector: S): Maybe<T>;

  /**
   * Returns array item satisfied given predicate or undefined
   */
  findBy(array: T[], predicate: PredicateFn<T>): T;

  /**
   * Merges given array with another one in order using provided function.
   * Arrays should have the same length.
   */
  orderedMergeWith(array: T[], another: T[], merger: ArrayEntityMerge<T, S>): T[];

  /**
   * Merges given array with another one using provided function.
   * This function merges elements having the same selector.
   */
  mergeWith(array: T[], another: T[], merger: ArrayEntityMerge<T, S>): T[];
}

export function createArrayHelper<T>(): ArrayHelper<T>;
export function createArrayHelper<T, S>(getSelector: TransformFn<T, S>): ArrayHelper<T, S>;
export function createArrayHelper(getSelector?: TransformFn<any, any>): ArrayHelper<any, any> {
  const getUnifiedSelector: TransformFn<any, any> = getSelector || identity;
  const indexOf: (array: any[], selector: any) => number = getSelector
    // find index using selector
    ? (array, selector) => findIndex(array, (el) => getSelector(el) === selector)
    // find index using standard indexOf function
    : (array, selector) => array.indexOf(selector);

  const prepend = (array: any[], ...items: any[]) => items.concat(array);
  const append = (array: any[], ...items: any[]) => array.concat(items);
  const insert = (array: any[], index: number, item: any) =>
    array.slice(0, index).concat([item]).concat(array.slice(index));
  const insertSorted = (array: any[], item: any, sortBy: TransformFn<any, any> | string) => {
    const index = sortedIndexBy(array, item, sortBy);
    return array.slice(0, index).concat([item]).concat(array.slice(index));
  };
  const update = (array: any[], selector: any, updater: any) => {
    const index = indexOf(array, selector);
    if (index < 0) {
      throw new Error(`Element satisfying selector ${selector} was not found`);
    }

    const newItem = isFunction(updater) ? updater(array[index], selector) : updater;
    return array[index] === newItem ? array : array.slice(0, index).concat([newItem]).concat(array.slice(index + 1));
  };
  const updateAll = (array: any[], transform: (item: any, index: number) => any) => {
    let cloned = false;
    return array.reduce(
      (clonedArray, item, i) => {
        const clonedItem = transform(item, i);
        if (clonedItem !== item) {
          // if item is changed => we ensure that array is cloned
          if (!cloned) {
            clonedArray = array.concat();
            cloned = true;
          }
          clonedArray[i] = clonedItem;
        }
        return clonedArray;
      },
      array);
  };
  const updateAllRight = (array: any[], transform: (item: any, index: number) => any) => {
    let cloned = false;
    return reduceRight(
      array,
      (clonedArray, item, i) => {
        const clonedItem = transform(item, i);
        if (clonedItem !== item) {
          // if item is changed => we ensure that array is cloned
          if (!cloned) {
            clonedArray = array.concat();
            cloned = true;
          }
          clonedArray[i] = clonedItem;
        }
        return clonedArray;
      },
      array);
  };
  const merge = (array: any[], selector: any, merger: any | ArrayEntityTransform<any, any>) => {
    const index = indexOf(array, selector);
    if (index < 0) {
      throw new Error(`Element satisfying selector ${selector} was not found`);
    }

    const newItem = isFunction(merger) ? merger(array[index], selector) : setEntityFields(array[index], merger);
    return array[index] === newItem ? array : array.slice(0, index).concat([newItem]).concat(array.slice(index + 1));
  };
  const remove = (array: any[], selector: any) => {
    const index = indexOf(array, selector);
    if (index < 0) {
      throw new Error(`Element satisfying selector ${selector} was not found`);
    }

    return array.slice(0, index).concat(array.slice(index + 1));
  };
  const move = (array: any[], fromIndex: number, toIndex: number) => {
    const result = array.concat();
    result.splice(fromIndex, 1);
    result.splice(toIndex, 0, array[fromIndex]);
    return result;
  };

  const find = (array: any[], selector: any) => {
    const index = indexOf(array, selector);
    return index < 0 ? void 0 : array[index];
  };

  const findBy = (array: any[], predicate: PredicateFn<any>) => array.find(predicate);

  const orderedMergeWith = (array: any[], another: any[], merger: ArrayEntityMerge<any, any>) => {
    if (array.length !== another.length) {
      throw new Error('ArrayHelper: arrays cannot be merged, because they have different length');
    }

    const length = array.length;
    let copied: Maybe<any[]>;
    for (let i = 0; i < length; i++) {
      const item = array[i];
      const newItem = merger(item, another[i], getUnifiedSelector(item));
      // copy array only if item was changed after merge
      if (copied) {
        copied.push(newItem);
      } else if (item !== newItem) {
        copied = array.slice(0, i);
        copied.push(newItem);
      }
    }

    return copied || array;
  };

  const mergeWith = (array: any[], another: any[], merger: ArrayEntityMerge<any, any>) => {
    const anotherIndex = keyBy(another, getUnifiedSelector);

    const length = array.length;
    let copied: Maybe<any[]>;
    for (let i = 0; i < length; i++) {
      const item = array[i];
      const anotherItem = anotherIndex[getUnifiedSelector(item)];

      const newItem = anotherItem ? merger(item, anotherItem, getUnifiedSelector(item)) : item;
      // copy array only if item was changed after merge
      if (copied) {
        copied.push(newItem);
      } else if (item !== newItem) {
        copied = array.slice(0, i);
        copied.push(newItem);
      }
    }

    return copied || array;
  };

  return {
    prepend, append, insert, insertSorted, update, updateAll, updateAllRight, merge, remove, move,
    indexOf, find, findBy, orderedMergeWith, mergeWith,
  };
}
